import { Flex, notify, Tag, Typography } from 'front-commons/ds';
import { useFetch } from 'front-commons/hooks';
import { useEffect, useState } from 'react';
import DistributorsTabs from 'containers/DistributorsTabs';
import { findSelectedDistributors, orderOrAddDistributors } from 'services/pos';
import { BUDataParams, CatalogDistributorChangeDrawerProps, DistributorsByBUParams } from './interfaces';
import DrawerDistributorSkeleton from './components/Skeleton';
import { getDistributorsPromotion } from 'services/products';
import { BUDataHandler } from './helpers';
import usePos from 'stores/pos';
import useDrawer from 'stores/drawer';
import { CustomError } from 'shared/errors';
import useBasket from 'stores/basket';
import useCustomer from 'stores/customer';
import { CustomerAccess } from 'stores/customer/interfaces';
import { getTags } from 'hooks/useCatalogProductRules/helpers';
import { DistributorsActionButtons } from './components/ActionButtons';
import { ProductParams } from 'services/products/interfaces';

const initialBUData: DistributorsByBUParams = {
	id: '',
	name: '',
	distributors: [],
	pedEnabled: false,
};

export default function DistributorChangeCatalog({
	promotionId,
	onAfterChangeDistributors,
}: CatalogDistributorChangeDrawerProps) {
	const [BUData, setBUData] = useState<BUDataParams>({
		selected: initialBUData,
		available: initialBUData,
	});
	const [promotionDetails, setPromotionDetails] = useState({} as Pick<ProductParams, 'description' | 'endDate'>);
	const [shouldShowEmptyColumns, setSHouldShowEmptyColumns] = useState(false);
	const [updatingDistributos, setUpdatingDistributors] = useState(false);

	const {
		posStore: { selectedPos },
		interceptDistributorRemoval,
		handleUpdateDistributor,
	} = usePos();
	const { simulatedViewType } = useCustomer();

	const { handleOpenDrawer, handleCloseDrawer, handleCloseAllDrawers } = useDrawer();

	const { handleGetBasketData } = useBasket();

	const { fetch: handleGetSelectedDistributors, loading: loadingSelected } = useFetch({
		fetchFunction: findSelectedDistributors, // * POS Distributors
		fetchParams: { posId: selectedPos.id },
	});

	const { fetch: handleGetDistributorByPromotion, loading: loadingAll } = useFetch({
		fetchFunction: getDistributorsPromotion, // * all distributors available for product
		fetchParams: { posId: selectedPos.id, promotionId },
	});

	const handleGetDistributorsList = async () => {
		const [selectedDistributorsForPOS, BUAndDistributorsAvailable] = await Promise.all([
			handleGetSelectedDistributors(),
			handleGetDistributorByPromotion(),
		]);

		if (BUAndDistributorsAvailable?.distributors.length) setSHouldShowEmptyColumns(true);

		setPromotionDetails({
			description: BUAndDistributorsAvailable?.description || '',
			endDate: BUAndDistributorsAvailable?.endDate,
		});

		setBUData({
			selected:
				selectedDistributorsForPOS?.reduce((acc, { businessUnitId, businessUnitName, ...rest }) => {
					if (acc) return acc;

					if (businessUnitId === BUAndDistributorsAvailable?.businessUnitId) {
						return {
							id: businessUnitId,
							name: businessUnitName,
							...rest,
						} as DistributorsByBUParams;
					}

					return acc;
				}, undefined as DistributorsByBUParams | undefined) || initialBUData,
			available: {
				id: BUAndDistributorsAvailable?.businessUnitId || '',
				name: BUAndDistributorsAvailable?.businessUnitName || '',
				distributors: BUAndDistributorsAvailable?.distributors || [],
				pedEnabled: false,
			},
		});
	};

	const handleChangeDistributor = async (type: 'add' | 'reorder' | 'remove', data: Record<string, any>) => {
		try {
			if (type === 'remove') {
				await interceptDistributorRemoval({
					businessUnitId: BUData.selected.id,
					distributorId: data.distributorId,
				});
			}

			setBUData((prevState) => {
				switch (type) {
					case 'add': {
						return BUDataHandler.handleDistributorAdd(prevState, data);
					}

					case 'reorder': {
						return BUDataHandler.handleDistributorReorder(prevState, data);
					}

					case 'remove': {
						return BUDataHandler.handleDistributorRemove(prevState, data);
					}

					default:
						return { ...prevState };
				}
			});
		} catch (err) {
			if (err instanceof CustomError && err.code === 'open-cart') {
				handleCloseAllDrawers();
				await Promise.fake({ delay: 50 });
				handleOpenDrawer('side-basket-drawer');
			}

			console.log('cancel');
		}
	};

	const handleUpdatePOSDistributors = async () => {
		try {
			setUpdatingDistributors(true);

			await orderOrAddDistributors({
				id: selectedPos.id,
				data: {
					businessUnitId: BUData.selected.id,
					distributors: BUData.selected.distributors.map(({ id }, index) => ({
						distributorId: id,
						order: index + 1,
					})),
				},
			});

			notify.positive({
				description: 'Seleção de distribuidores atualizados com sucesso.',
			});

			await handleGetBasketData({ loading: 'refetch', initializeBefore: true });

			await handleUpdateDistributor(BUData.selected as any);

			handleCloseDrawer('catalog-distributor-change');

			onAfterChangeDistributors?.();
		} catch (err) {
			console.log(err);
			notify.negative({
				description: 'Não foi possível atualizar a lista de distribuidores! Por favor, tente novamente.',
			});
			setUpdatingDistributors(false);
		}
	};

	useEffect(() => {
		handleGetDistributorsList();
	}, []);

	return (
		<Flex direction="column" overflow="auto" height="100%" gap="16px" margin="0 0 72px">
			<Flex direction="column" padding="16px 16px 0" gap="8px">
				{getTags(promotionDetails)
					.filter(({ label }) => label.includes('restante'))
					.map((tag) => (
						<Tag {...tag} width="max-content" />
					))}

				<Typography variant="Paragraph/Semibold">{promotionDetails.description}</Typography>
			</Flex>

			{(loadingSelected || loadingAll) && <DrawerDistributorSkeleton />}

			{!(loadingSelected || loadingAll) && (
				<Flex overflow="auto" width="100%" padding="0 16px" gap="16px">
					<DistributorsTabs
						hideSearch
						isDrawer
						type="MASSIVE"
						sourceId={BUData.selected?.id}
						available={BUData.available as any}
						selected={BUData.selected as any}
						onAdd={(distributorId, positionIndex) => handleChangeDistributor('add', { distributorId, positionIndex })}
						onReorder={(from, to) => handleChangeDistributor('reorder', { from, to })}
						onRemove={(distributorId) => handleChangeDistributor('remove', { distributorId })}
						forceShowColumns={shouldShowEmptyColumns}
						emptyAvailableDistributorsMessage={
							<strong>Não foram encontrados distribuidores que atendam à promoção selecionada.</strong>
						}
						alertMessage={
							BUData.available.distributors.length
								? 'A alteração dos distribuidores pode afetar o seu carrinho e suas próximas compras.'
								: undefined
						}
						customDescription="Sua farmácia precisa ter um pré-cadastro no distribuidor para realizar seu pedido."
						CustomCTA={
							<DistributorsActionButtons
								textButton={{ children: 'Cancelar', onClick: () => handleCloseDrawer('catalog-distributor-change') }}
								rightButton={{
									children: 'Salvar alterações',
									onClick: handleUpdatePOSDistributors,
									loading: updatingDistributos,
									disabled: simulatedViewType() === CustomerAccess.BASIC || !BUData.selected.distributors.length,
									width: '196px',
								}}
								width="100%"
								justifyContent="flex-end"
								bottom="0"
							/>
						}
					/>
				</Flex>
			)}
		</Flex>
	);
}
